// MaturaLinearNoZeroClosedTask.js
// Zadanie maturalne zamkniete: funkcja liniowa nie ma miejsca zerowego.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function uniqueIntegers(base, min, max, count) {
  const out = new Set([base]);
  while (out.size < count) {
    out.add(randInt(min, max));
  }
  return [...out];
}

function fmtSigned(n) {
  return n >= 0 ? `+${n}` : `${n}`;
}

function fmtMFirst(A) {
  return A >= 0 ? `m-${A}` : `m+${Math.abs(A)}`;
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaLinearNoZeroClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 13,
    },
    data: {
      A: 3,
      B: -4,
      options: [
        { label: "A", value: -3, latex: "-3", isCorrect: false },
        { label: "B", value: 0, latex: "0", isCorrect: false },
        { label: "C", value: 3, latex: "3", isCorrect: true },
        { label: "D", value: 4, latex: "4", isCorrect: false },
      ],
      correctIndex: 2,
      useMFirst: false,
      maxPoints: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  // f(x) = (A - m)x + B, gdzie B != 0.
  // Brak miejsca zerowego zachodzi tylko gdy wspolczynnik przy x jest rowny 0,
  // czyli m = A, a wtedy funkcja stale ma wartosc B != 0.
  let A = 0;
  while (A === 0) A = randInt(-9, 9);

  let B = 0;
  while (B === 0) B = randInt(-9, 9);

  const correctM = A;

  // Distractory: bliskie poprawnej + typowe "odruchowe" 0.
  const pool = uniqueIntegers(correctM, -10, 10, 12).filter((v) => v !== correctM);
  const distractors = [correctM - 1, correctM + 1, 0]
    .filter((v, i, arr) => v !== correctM && arr.indexOf(v) === i);

  while (distractors.length < 3) {
    const v = pick(pool);
    if (!distractors.includes(v)) distractors.push(v);
  }

  const rawOptions = [
    { value: correctM, isCorrect: true },
    { value: distractors[0], isCorrect: false },
    { value: distractors[1], isCorrect: false },
    { value: distractors[2], isCorrect: false },
  ].sort(() => Math.random() - 0.5);

  const useMFirst = Math.random() < 0.5;

  const options = rawOptions.map((opt, idx) => ({
    label: LETTERS[idx],
    value: opt.value,
    latex: `${opt.value}`,
    isCorrect: opt.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLinearNoZeroClosedTask",
    data: {
      A,
      B,
      options,
      correctIndex,
      useMFirst,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { A, B, options, useMFirst } = data;
  const coef = useMFirst ? fmtMFirst(A) : `${A}-m`;
  const expr = `f(x)=(${coef})x${fmtSigned(B)}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>
      <p>
        Funkcja liniowa <InlineMath math={"f"} /> jest określona wzorem{" "}
        <InlineMath math={expr} />.
      </p>
      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Funkcja <InlineMath math={"f"} /> <u>nie ma</u> miejsca zerowego dla{" "}
        <InlineMath math={"m"} /> równego:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { A, B, useMFirst } = data;
  const mFirst = fmtMFirst(A);
  const coef = useMFirst ? mFirst : `${A}-m`;
  const solve = useMFirst ? `${mFirst}=0\\Rightarrow m=${A}` : `${A}-m=0\\Rightarrow m=${A}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        Dla funkcji liniowej <InlineMath math={"f(x)=ax+b"} />:
      </p>
      <ul style={{ marginLeft: 18 }}>
        <li>
          gdy <InlineMath math={"a\\neq 0"} />, funkcja ma dokładnie jedno miejsce zerowe;
        </li>
        <li>
          gdy <InlineMath math={"a=0"} />, funkcja jest stała <InlineMath math={"f(x)=b"} />.
        </li>
      </ul>

      <p>
        Tutaj <InlineMath math={`a=${coef}`} /> oraz <InlineMath math={`b=${B}`} />, przy czym{" "}
        <InlineMath math={`${B}\\neq 0`} />.
      </p>

      <p>
        Aby funkcja nie miała miejsca zerowego, musi być stała i niezerowa, czyli:
      </p>
      <BlockMath math={solve} />

      <p>
        Wtedy <InlineMath math={`f(x)=${B}`} /> dla każdego{" "}
        <InlineMath math={"x"} />, więc nie ma rozwiązania równania{" "}
        <InlineMath math={"f(x)=0"} />.
      </p>
    </div>
  );
}
